import { useCallback, useEffect, useRef, useState, useTransition } from 'react';
import { useTranslations } from '@/i18n/translations';
import {
  Body, Button, Caption, Heading, I, Dialog, DialogFooter, DialogSection, Row, Stack, Textarea,
} from '@leanin/ui';
import { DropZone } from '@leanin/ui/client';

import { PostMediaUploadButton } from '@/components/feed/PostMediaUploadButton';
import { formatAttachmentBytes } from '@/components/media/format-attachment-bytes';
import { StagedUploadChip } from '@/components/media/StagedUploadChip';
import { useStagedUploads } from '@/components/media/use-staged-uploads';
import type { BugReportScreenshot } from '@/lib/mutations/bug-reports';
import { usePresignFeedPostMedia } from '@/lib/mutations/feed';
import { uploadWithProgress } from '@/lib/upload';

const DESCRIPTION_MAX = 5000;
const MAX_SCREENSHOTS = 3;
// Mirrors the bug-report endpoint's per-screenshot cap; the shared presign
// alone would admit files up to the posts cap.
const MAX_SCREENSHOT_BYTES = 10 * 1024 * 1024;
const SCREENSHOT_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const;
type ScreenshotType = (typeof SCREENSHOT_TYPES)[number];
const SCREENSHOT_ACCEPT = SCREENSHOT_TYPES.join(',');

function isScreenshotType(type: string): type is ScreenshotType {
  return (SCREENSHOT_TYPES as readonly string[]).includes(type);
}

function pastedFiles(event: ClipboardEvent): File[] {
  const items = event.clipboardData?.items;
  if (items === undefined) return [];
  return Array.from(items).flatMap((item) => {
    if (item.kind !== 'file') return [];
    const file = item.getAsFile();
    return file === null ? [] : [file];
  });
}

export interface ReportBugModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (input: {
    description: string;
    screenshots: readonly BugReportScreenshot[];
  }) => Promise<{ ok: true } | { ok: false; message: string }>;
}

export function ReportBugModal({ open, onClose, onSubmit }: ReportBugModalProps) {
  const t = useTranslations('Feedback');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [attachError, setAttachError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [isPending, startTransition] = useTransition();
  // Set synchronously in `submit` — `isPending` alone leaves a window before
  // the pending render commits where an Escape would dismiss mid-flight,
  // wiping the description via the close-reset effect and landing the
  // result on a closed dialog.
  const submitInFlightRef = useRef(false);

  const presignMedia = usePresignFeedPostMedia();
  const screenshots = useStagedUploads<BugReportScreenshot>({
    maxCount: MAX_SCREENSHOTS,
    rejectReason: (file) => {
      if (!isScreenshotType(file.type)) return t('reportBug.screenshotUnsupportedType');
      if (file.size > MAX_SCREENSHOT_BYTES) {
        return t('reportBug.screenshotTooLarge', {
          max: formatAttachmentBytes(MAX_SCREENSHOT_BYTES),
        });
      }
      return null;
    },
    upload: async (file, { onProgress, signal }) => {
      // Safe cast: rejectReason admits only SCREENSHOT_TYPES.
      const contentType = file.type as ScreenshotType;
      const presign = await presignMedia.mutateAsync({ contentType, sizeBytes: file.size });
      await uploadWithProgress({
        url: presign.uploadUrl,
        body: file,
        headers: presign.fields,
        onProgress,
        signal,
      });
      return { key: presign.key, contentType, sizeBytes: file.size };
    },
    uploadFailedMessage: t('reportBug.screenshotUploadFailed'),
  });
  const { add: addScreenshots, clear: clearScreenshots } = screenshots;

  const attach = useCallback(
    (files: readonly File[]): void => {
      if (submitInFlightRef.current) return;
      const { overflow } = addScreenshots(files);
      setAttachError(
        overflow > 0 ? t('reportBug.screenshotsTooMany', { max: MAX_SCREENSHOTS }) : null,
      );
    },
    [addScreenshots, t],
  );

  useEffect(() => {
    if (!open) {
      setDescription('');
      setError(null);
      setAttachError(null);
      setSuccess(false);
      clearScreenshots();
    }
  }, [open, clearScreenshots]);

  // Listens on the document rather than the form so a paste lands wherever
  // focus sits inside the modal — the header close button, the footer
  // actions, or the textarea. Image pastes win over any text riding along on
  // the clipboard (a Finder file copy also carries the file name as text).
  useEffect(() => {
    if (!open || success) return;
    const onPaste = (event: ClipboardEvent): void => {
      const files = pastedFiles(event);
      if (files.length === 0) return;
      event.preventDefault();
      attach(files);
    };
    document.addEventListener('paste', onPaste);
    return () => document.removeEventListener('paste', onPaste);
  }, [open, success, attach]);

  // The DropZone can only wrap the dialog body (Dialog splits header, body
  // and footer into sibling sections). A file dropped anywhere else would get
  // the browser default — opening the image and navigating away from the
  // member's unsent report — so swallow file drags document-wide while open.
  useEffect(() => {
    if (!open) return;
    const swallowFileDrag = (event: DragEvent): void => {
      if (event.dataTransfer?.types.includes('Files')) event.preventDefault();
    };
    document.addEventListener('dragover', swallowFileDrag);
    document.addEventListener('drop', swallowFileDrag);
    return () => {
      document.removeEventListener('dragover', swallowFileDrag);
      document.removeEventListener('drop', swallowFileDrag);
    };
  }, [open]);

  const submit = () => {
    if (submitInFlightRef.current || screenshots.uploading) return;
    setError(null);
    const trimmed = description.trim();
    if (trimmed.length === 0) {
      setError(t('reportBug.errorEmpty'));
      return;
    }
    const ready = screenshots.staged.flatMap((s) =>
      s.state.status === 'ready' ? [s.state.result] : [],
    );
    submitInFlightRef.current = true;
    startTransition(async () => {
      try {
        const result = await onSubmit({ description: trimmed, screenshots: ready });
        if (!result.ok) {
          setError(result.message);
          return;
        }
        setSuccess(true);
      } finally {
        submitInFlightRef.current = false;
      }
    });
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => { if (!v) onClose(); }}
      canDismiss={() => !submitInFlightRef.current && !isPending}
      size="md"
      accessibleTitle={t('reportBug.title')}
    >
      <DialogSection bleed>
        <Row justify="space-between" align="center" gap="lg" py="lg" px="lg">
          <Heading level={3}>{t('reportBug.title')}</Heading>
          <Button
            variant="ghost"
            size="sm"
            icon={I.x}
            aria-label={t('reportBug.closeAria')}
            onClick={onClose}
            disabled={isPending}
          />
        </Row>
      </DialogSection>

      <DialogSection scrollable divider>
        {success ? (
          <Body>{t('reportBug.successBody')}</Body>
        ) : (
          <DropZone
            onFiles={attach}
            disabled={isPending}
            overlayLabel={t('reportBug.screenshotsDropOverlay')}
          >
            <Stack gap="lg">
              <Stack gap="sm">
                <Textarea
                  rows={6}
                  aria-label={t('reportBug.fieldLabel')}
                  value={description}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v.length <= DESCRIPTION_MAX) setDescription(v);
                  }}
                  placeholder={t('reportBug.placeholder')}
                />
                {description.length >= DESCRIPTION_MAX - 500 && (
                  <Row justify="flex-end" align="center">
                    <Caption>
                      {description.length} / {DESCRIPTION_MAX}
                    </Caption>
                  </Row>
                )}
              </Stack>

              <Stack gap="sm">
                <Row>
                  <PostMediaUploadButton
                    variant="inline"
                    label={t('reportBug.screenshotsAdd')}
                    accept={SCREENSHOT_ACCEPT}
                    onFiles={attach}
                    disabled={isPending || screenshots.staged.length >= MAX_SCREENSHOTS}
                  />
                </Row>
                {screenshots.staged.length > 0 && (
                  <Row gap="sm" wrap>
                    {screenshots.staged.map((s, i) => (
                      <StagedUploadChip
                        key={s.id}
                        upload={s}
                        detail={
                          s.state.status === 'uploading'
                            ? t('reportBug.screenshotUploading', {
                                percent: Math.round(s.state.progress),
                              })
                            : formatAttachmentBytes(s.sizeBytes)
                        }
                        previewAlt={t('reportBug.screenshotAlt', { index: i + 1 })}
                        removeLabel={t('reportBug.screenshotRemoveAria', { index: i + 1 })}
                        onRemove={() => {
                          screenshots.remove(s.id);
                          setAttachError(null);
                        }}
                      />
                    ))}
                  </Row>
                )}
                {attachError !== null && (
                  <span role="alert">
                    <Body size="sm" color="destructive">
                      {attachError}
                    </Body>
                  </span>
                )}
              </Stack>

              <Caption>{t('reportBug.disclosure')}</Caption>

              {error !== null && (
                <span role="alert" aria-live="polite">
                  <Body color="destructive">
                    {error}
                  </Body>
                </span>
              )}
            </Stack>
          </DropZone>
        )}
      </DialogSection>

      <DialogFooter>
        <Row gap="md" align="center">
          {success ? (
            <Button variant="primary" onClick={onClose}>
              {t('reportBug.close')}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={isPending}>
                {t('reportBug.cancel')}
              </Button>
              <Button
                variant="primary"
                iconRight={I.send}
                onClick={submit}
                loading={isPending}
                disabled={screenshots.uploading}
              >
                {t('reportBug.submit')}
              </Button>
            </>
          )}
        </Row>
      </DialogFooter>
    </Dialog>
  );
}
